import type { CSSProperties, ReactNode } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';

// ===== Page chrome =====

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <header
      style={{
        padding: '24px 20px 12px',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1
          className="tx-title"
          style={{
            margin: 0,
            fontSize: 28,
            lineHeight: 1.15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: '6px 0 0',
              fontSize: 14,
              color: 'var(--tg-subtitle)',
              fontWeight: 400,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {right}
    </header>
  );
}

// ===== Section / Cell =====

export function Section({
  title,
  footer,
  children,
}: {
  title?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section style={{ padding: '0 16px', marginTop: 18 }} className="fade-in">
      {title && (
        <h2
          className="tx-overline"
          style={{
            margin: '0 16px 10px',
          }}
        >
          {title}
        </h2>
      )}
      <div
        style={{
          background: 'var(--tg-section-bg)',
          borderRadius: 'var(--r-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--sh-card)',
        }}
      >
        {children}
      </div>
      {footer && (
        <p
          style={{
            margin: '10px 16px 0',
            fontSize: 12.5,
            color: 'var(--tg-subtitle)',
            lineHeight: 1.5,
          }}
        >
          {footer}
        </p>
      )}
    </section>
  );
}

export function Cell({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
  destructive,
  last,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  last?: boolean;
}) {
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderBottom: last ? 'none' : '0.5px solid var(--tg-separator)',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'background var(--tr-fast)',
        color: destructive ? 'var(--tg-destructive)' : 'inherit',
        WebkitTapHighlightColor: 'transparent',
      }}
      onPointerDown={(e) => {
        if (interactive) (e.currentTarget as HTMLDivElement).style.background = 'var(--action-active)';
      }}
      onPointerUp={(e) => ((e.currentTarget as HTMLDivElement).style.background = '')}
      onPointerCancel={(e) => ((e.currentTarget as HTMLDivElement).style.background = '')}
      onPointerLeave={(e) => ((e.currentTarget as HTMLDivElement).style.background = '')}
    >
      {leading && <div style={{ flexShrink: 0, display: 'flex' }}>{leading}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15.5,
            fontWeight: 500,
            letterSpacing: '-0.005em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--tg-hint)',
              marginTop: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {trailing && (
        <div
          style={{
            color: 'var(--tg-hint)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14.5,
          }}
        >
          {trailing}
        </div>
      )}
      {interactive && !trailing && (
        <ChevronRight size={18} color="var(--tg-hint)" style={{ opacity: 0.4 }} />
      )}
    </div>
  );
}

// ===== Button =====

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';

export function Button({
  children,
  onClick,
  disabled,
  loading,
  variant = 'primary',
  leading,
  full = true,
  size = 'md',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: BtnVariant;
  leading?: ReactNode;
  full?: boolean;
  size?: 'sm' | 'md' | 'lg';
}) {
  const padding = size === 'sm' ? '10px 14px' : size === 'lg' ? '16px 22px' : '13px 18px';
  const fontSize = size === 'sm' ? 14 : size === 'lg' ? 16 : 15;

  const variantStyle: Record<BtnVariant, CSSProperties> = {
    primary: {
      background: 'var(--brand)',
      color: '#fff',
      boxShadow: '0 1px 2px rgba(7,141,238,0.18), 0 6px 14px -4px rgba(7,141,238,0.32)',
    },
    secondary: {
      background: 'var(--tg-secondary-bg)',
      color: 'var(--tg-text)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--brand)',
    },
    soft: {
      background: 'var(--brand-soft)',
      color: 'var(--brand)',
    },
    danger: {
      background: 'var(--danger)',
      color: '#fff',
      boxShadow: '0 1px 2px rgba(255,69,58,0.18), 0 6px 14px -4px rgba(255,69,58,0.32)',
    },
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        ...variantStyle[variant],
        border: 'none',
        borderRadius: 'var(--r-md)',
        padding,
        fontSize,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        opacity: disabled ? 0.45 : 1,
        width: full ? '100%' : 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        transition: 'filter var(--tr-fast), transform 80ms var(--ease), opacity var(--tr-fast)',
        WebkitTapHighlightColor: 'transparent',
      }}
      onPointerDown={(e) => {
        if (!disabled && !loading) {
          (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(0.92)';
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.985)';
        }
      }}
      onPointerUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.filter = '';
        (e.currentTarget as HTMLButtonElement).style.transform = '';
      }}
      onPointerCancel={(e) => {
        (e.currentTarget as HTMLButtonElement).style.filter = '';
        (e.currentTarget as HTMLButtonElement).style.transform = '';
      }}
      onPointerLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.filter = '';
        (e.currentTarget as HTMLButtonElement).style.transform = '';
      }}
    >
      {loading ? <Spinner size={16} /> : leading}
      {children}
    </button>
  );
}

// ===== Status dot =====

export function StatusDot({ color, pulse = true }: { color: string; pulse?: boolean }) {
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    >
      {pulse && (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: color,
            animation: 'pulse-dot 1.8s var(--ease) infinite',
          }}
        />
      )}
    </span>
  );
}

// ===== Skeleton =====

export function Skeleton({
  width = '100%',
  height = 16,
  radius = 8,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
}) {
  return (
    <span
      style={{
        display: 'inline-block',
        width,
        height,
        borderRadius: radius,
        background:
          'linear-gradient(90deg, rgba(145,158,171,0.10) 0%, rgba(145,158,171,0.22) 50%, rgba(145,158,171,0.10) 100%)',
        backgroundSize: '936px 100%',
        animation: 'shimmer 1.6s linear infinite',
      }}
    />
  );
}

// ===== Spinner =====

export function Spinner({ size = 20 }: { size?: number }) {
  return <Loader2 size={size} style={{ animation: 'spin 0.9s linear infinite' }} />;
}

// ===== Avatar =====

const AVATAR_COLORS = [
  ['#FF9F0A', '#FFB84A'],
  ['#FF453A', '#FF7A6F'],
  ['#AF52DE', '#C778EE'],
  ['#5856D6', '#7B7AE0'],
  ['#0A84FF', '#3DA3FF'],
  ['#5AC8FA', '#7FD6FB'],
  ['#22C55E', '#5BD581'],
  ['#FFD60A', '#FFE445'],
];

export function Avatar({
  label,
  size = 40,
  icon,
  emoji,
  src,
}: {
  label?: string;
  size?: number;
  icon?: ReactNode;
  emoji?: string;
  /** External image URL — takes priority over emoji/initials. */
  src?: string;
}) {
  const hash = label
    ? [...label].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length
    : 0;
  const palette = AVATAR_COLORS[hash] ?? ['#919EAB', '#B8C0CC'];
  const [c1, c2] = palette;
  const initials = label ? label.slice(0, 2).toUpperCase() : '';
  const isEmoji = !!emoji;
  if (src) {
    return (
      <img
        src={src}
        alt={label ?? ''}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          background: 'var(--tg-secondary-bg)',
          boxShadow: '0 1px 2px rgba(28,37,46,0.08)',
        }}
        onError={(e) => {
          // hide broken image; let parent fall back if needed
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background:
          icon
            ? 'var(--tg-secondary-bg)'
            : isEmoji
              ? 'var(--tg-secondary-bg)'
              : `linear-gradient(135deg, ${c1}, ${c2})`,
        color: icon ? 'var(--tg-text)' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isEmoji ? size * 0.6 : size * 0.34,
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: '-0.02em',
        lineHeight: 1,
        boxShadow: isEmoji || icon ? 'none' : '0 1px 2px rgba(28,37,46,0.08)',
      }}
    >
      {icon ?? emoji ?? initials}
    </div>
  );
}

// ===== Empty state =====

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className="fade-in"
      style={{
        padding: '56px 32px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 68,
          height: 68,
          borderRadius: '50%',
          background: 'var(--brand-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--brand)',
          marginBottom: 4,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</div>
      {description && (
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: 'var(--tg-hint)',
            maxWidth: 280,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      )}
      {action && <div style={{ marginTop: 16, width: '100%', maxWidth: 240 }}>{action}</div>}
    </div>
  );
}

// ===== Text Input =====

export function TextInput(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  trailing?: ReactNode;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'url';
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--tg-secondary-bg)',
        borderRadius: 'var(--r-md)',
        padding: '12px 14px',
        transition: 'background var(--tr-fast), box-shadow var(--tr-fast)',
      }}
      onFocus={(e) =>
        ((e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 3px var(--brand-glow)')
      }
      onBlur={(e) => ((e.currentTarget as HTMLDivElement).style.boxShadow = '')}
    >
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          fontSize: 15.5,
          color: 'var(--tg-text)',
          minWidth: 0,
        }}
      />
      {props.trailing}
    </div>
  );
}

// ===== Segmented control =====

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        background: 'var(--tg-secondary-bg)',
        borderRadius: 'var(--r-md)',
        padding: 3,
        gap: 3,
      }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            flex: 1,
            padding: '8px 10px',
            borderRadius: 'calc(var(--r-md) - 3px)',
            background: value === o.value ? 'var(--tg-section-bg)' : 'transparent',
            color: value === o.value ? 'var(--tg-text)' : 'var(--tg-hint)',
            fontSize: 13.5,
            fontWeight: value === o.value ? 600 : 500,
            boxShadow: value === o.value ? '0 1px 2px rgba(28,37,46,0.08)' : 'none',
            transition: 'all var(--tr-fast)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ===== Progress bar =====

export function ProgressBar({ pct }: { pct: number }) {
  const safe = Math.max(0, Math.min(100, pct));
  // Solid color (no gradient w/ alpha) — CSS vars can't be concatenated with "b8" etc.
  const color =
    safe >= 90 ? 'var(--danger)' : safe >= 70 ? 'var(--warning)' : 'var(--success)';
  return (
    <div
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height: 6,
        background: 'var(--tg-separator)',
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: color,
          borderRadius: 999,
          transformOrigin: 'left center',
          transform: `scaleX(${safe / 100})`,
          transition: 'transform 0.5s var(--ease)',
        }}
      />
    </div>
  );
}

// ===== Chip / Pill =====

export function Chip({
  children,
  color = 'neutral',
  leading,
}: {
  children: ReactNode;
  color?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger';
  leading?: ReactNode;
}) {
  const palette: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: 'var(--tg-secondary-bg)', fg: 'var(--tg-text)' },
    brand: { bg: 'var(--brand-soft)', fg: 'var(--brand)' },
    success: { bg: 'var(--success-soft)', fg: 'var(--success)' },
    warning: { bg: 'var(--warning-soft)', fg: 'var(--warning)' },
    danger: { bg: 'var(--danger-soft)', fg: 'var(--danger)' },
  };
  const c = palette[color] ?? palette.neutral!;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: c.bg,
        color: c.fg,
        padding: '4px 10px',
        borderRadius: 'var(--r-full)',
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
      }}
    >
      {leading && <span style={{ display: 'inline-flex' }}>{leading}</span>}
      {children}
    </span>
  );
}
